
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * Version struct
 */
class Version {
  /**
   * Initialize struct
   * @param {!number=} major
   * @param {!number=} minor
   * @param {!number=} revision
   * @param {!number=} build
   * @constructor
   */
  constructor (major = 0, minor = 0, revision = 0, build = 0) {
    this.Major = major
    this.Minor = minor
    this.Revision = revision
    this.Build = build
  }

  /**
   * Copy struct (shallow copy)
   * @this {!Version}
   * @param {!Version} other Other struct
   * @returns {!Version} This struct
   */
  copy (other) {
    if (other.Major != null) {
      this.Major = other.Major
    } else {
      this.Major = null
    }
    if (other.Minor != null) {
      this.Minor = other.Minor
    } else {
      this.Minor = null
    }
    if (other.Revision != null) {
      this.Revision = other.Revision
    } else {
      this.Revision = null
    }
    if (other.Build != null) {
      this.Build = other.Build
    } else {
      this.Build = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!Version}
   * @returns {!Version} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new VersionModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new VersionModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!Version}
   * @param {!Version} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof Version)) {
      throw new TypeError('Instance of Version is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!Version}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Major: ((this.Major != null) ? this.Major : null),
      Minor: ((this.Minor != null) ? this.Minor : null),
      Revision: ((this.Revision != null) ? this.Revision : null),
      Build: ((this.Build != null) ? this.Build : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return Version.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!Version} other Object value
   * @returns {!Version} Created struct
   */
  static fromObject (other) {
    return new Version().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!Version}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return Version.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!Version}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 96
  }
}

export { Version };

/**
 * Fast Binary Encoding Version field model
 */
class FieldModelVersion extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Major = new fbe.FieldModelUInt32(buffer, 4 + 4)
    this.Minor = new fbe.FieldModelUInt32(buffer, this.Major.fbeOffset + this.Major.fbeSize)
    this.Revision = new fbe.FieldModelUInt32(buffer, this.Minor.fbeOffset + this.Minor.fbeSize)
    this.Build = new fbe.FieldModelUInt32(buffer, this.Revision.fbeOffset + this.Revision.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelVersion}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelVersion}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Major.fbeSize + this.Minor.fbeSize + this.Revision.fbeSize + this.Build.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelVersion}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Major.fbeExtra + this.Minor.fbeExtra + this.Revision.fbeExtra + this.Build.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelVersion}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelVersion.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelVersion}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 96
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelVersion}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelVersion}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Major.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Major.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Major.fbeSize

    if ((fbeCurrentSize + this.Minor.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Minor.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Minor.fbeSize

    if ((fbeCurrentSize + this.Revision.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Revision.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Revision.fbeSize

    if ((fbeCurrentSize + this.Build.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Build.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Build.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelVersion}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelVersion}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelVersion}
   * @param {!Version} fbeValue Default value, defaults is new Version()
   * @returns {!Version} Version value
   */
  get (fbeValue = new Version()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelVersion}
   * @param {!Version} fbeValue Version value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Major.fbeSize) <= fbeStructSize) {
      fbeValue.Major = this.Major.get()
    } else {
      fbeValue.Major = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Major.fbeSize

    if ((fbeCurrentSize + this.Minor.fbeSize) <= fbeStructSize) {
      fbeValue.Minor = this.Minor.get()
    } else {
      fbeValue.Minor = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Minor.fbeSize

    if ((fbeCurrentSize + this.Revision.fbeSize) <= fbeStructSize) {
      fbeValue.Revision = this.Revision.get()
    } else {
      fbeValue.Revision = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Revision.fbeSize

    if ((fbeCurrentSize + this.Build.fbeSize) <= fbeStructSize) {
      fbeValue.Build = this.Build.get()
    } else {
      fbeValue.Build = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Build.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelVersion}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelVersion}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelVersion}
   * @param {!Version} fbeValue Version value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelVersion}
   * @param {Version} fbeValue Version value
   */
  setFields (fbeValue) {
    this.Major.set(fbeValue.Major)
    this.Minor.set(fbeValue.Minor)
    this.Revision.set(fbeValue.Revision)
    this.Build.set(fbeValue.Build)
  }
}

export { FieldModelVersion };

/**
 * Fast Binary Encoding Version model
 */
class VersionModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelVersion(this.buffer, 4)
  }

  /**
   * Get the Version model
   * @this {!VersionModel}
   * @returns {!FieldModelVersion} model Version model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!VersionModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!VersionModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return VersionModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!VersionModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelVersion.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!VersionModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!VersionModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!VersionModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!VersionModel}
   * @param {!Version} value Version value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!VersionModel}
   * @param {!Version} value Version value, defaults is new Version()
   * @return {!object} Deserialized Version value and its size
   */
  deserialize (value = new Version()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new Version(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new Version(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!VersionModel}
   * @param {!number} prev Previous Version model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { VersionModel };
